import { useCallback } from 'react';

import { GA4_EVENT_ITEM_DELIVERY_PLP, NOT_APPLICABLE } from '@/data/constants/gtm';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { isB2BStore, useSettings } from '@/data/Settings';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';
import { logEventWithValues } from '@/data/utils/logEventsUtils';
import { useMobileApp } from '@/utils/useMobileApp';
import { B2B, B2C } from 'presentation/events/constants/eventConstants';
import { useCurrentPhysicalStoreId } from 'presentation/events/hooks/useCurrentPhysicalStoreId';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { triggerGA4EcommerceEvent } from 'presentation/events/services/ga4EcommerceService';
import { GA4ItemDeliveryPLPEventPayload } from 'presentation/events/types/ga4Types';

import { SendItemDeliveryPLPGA4EventProps } from './types';

export const useTrackItemDeliveryPLPGA4 = () => {
	const { settings } = useSettings();
	const { physicalStoreId } = useCurrentPhysicalStoreId();
	const { isMobileApp } = useMobileApp();
	const trackException = useTrackException();
	const isB2B = isB2BStore(settings);

	return useCallback(
		({ items }: SendItemDeliveryPLPGA4EventProps) => {
			try {
				const { ga4 } = getGTMConfig(settings);

				if (!ga4 || !physicalStoreId) return;

				const storeData = getStoreDataByPhysicalStoreId(physicalStoreId);

				const payload: GA4ItemDeliveryPLPEventPayload = {
					event: GA4_EVENT_ITEM_DELIVERY_PLP,
					ecommerce: {
						actual_shop_city: storeData?.city || NOT_APPLICABLE,
						actual_shop_name: storeData?.storeName || NOT_APPLICABLE,
						actual_shop_state: storeData?.stLocName || NOT_APPLICABLE,
						items,
					},
				};

				if (!isMobileApp) {
					triggerGA4EcommerceEvent(payload, { cleanup: true });
					return;
				}

				logEventWithValues(payload.event, {
					...payload.ecommerce,
					app_thd: !isB2B ? B2C : B2B,
				});
			} catch (e) {
				console.error('Error sending item delivery PLP event', e);
				trackException(e);
			}
		},
		[settings, physicalStoreId, isMobileApp, isB2B, trackException]
	);
};
